import { ref, computed, onMounted, onUnmounted, watchEffect } from "vue"

export interface CountdownState {
  days: number
  hours: number
  minutes: number
  seconds: number
  daysPadded: string
  hoursPadded: string
  minutesPadded: string
  secondsPadded: string
  totalMs: number
  totalSeconds: number
  endTime: Date
  isFinished: boolean
}

export interface UseCountdownOptions {
  endAt?: string | Date | null
  fallbackDays?: number
}

const addDays = (base: Date, n: number) => new Date(base.getTime() + n * 24 * 3600 * 1000)
const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)

export function useCountdown(opts: UseCountdownOptions = {}): CountdownState {
  const fallbackDays = opts.fallbackDays ?? 15
  const now = ref(new Date())
  let timer: number | undefined

  const resolveEnd = () => {
    const val = opts.endAt
    if (!val) return addDays(new Date(), fallbackDays)
    const dt = typeof val === "string" ? new Date(val) : val
    return isNaN(dt.getTime()) || Date.now() > dt.getTime() ? addDays(new Date(), fallbackDays) : dt
  }

  const endTime = ref<Date>(resolveEnd())

  // Keep endTime in sync with options.endAt changes (if reactive upstream)
  watchEffect(() => {
    endTime.value = resolveEnd()
  })

  onMounted(() => {
    timer = window.setInterval(() => (now.value = new Date()), 1000)
  })
  onUnmounted(() => {
    if (timer) window.clearInterval(timer)
  })

  const totalMsRef = computed(() => Math.max(0, endTime.value.getTime() - now.value.getTime()))
  const totalSecondsRef = computed(() => Math.floor(totalMsRef.value / 1000))
  const daysRef = computed(() => Math.floor(totalSecondsRef.value / (24 * 3600)))
  const hoursRef = computed(() => Math.floor((totalSecondsRef.value % (24 * 3600)) / 3600))
  const minutesRef = computed(() => Math.floor((totalSecondsRef.value % 3600) / 60))
  const secondsRef = computed(() => Math.floor(totalSecondsRef.value % 60))

  return {
    get days() {
      return daysRef.value
    },
    get hours() {
      return hoursRef.value
    },
    get minutes() {
      return minutesRef.value
    },
    get seconds() {
      return secondsRef.value
    },
    get daysPadded() {
      return pad(daysRef.value)
    },
    get hoursPadded() {
      return pad(hoursRef.value)
    },
    get minutesPadded() {
      return pad(minutesRef.value)
    },
    get secondsPadded() {
      return pad(secondsRef.value)
    },
    get totalMs() {
      return totalMsRef.value
    },
    get totalSeconds() {
      return totalSecondsRef.value
    },
    get endTime() {
      return endTime.value
    },
    get isFinished() {
      return totalMsRef.value <= 0
    }
  }
}
